import { useConnection } from 'hooks/useConnection'
import useCopyClipboard from 'hooks/useCopyClipboard'
import { addChainToUrl } from 'lib/utils/utils'
import React, { HTMLProps, useCallback } from 'react'
import { ArrowLeft, Clock, Copy, ExternalLink as LinkIconFeather, Trash, X } from 'react-feather'
import { Link, LinkProps, NavLink, NavLinkProps } from 'react-router-dom'
import styled, { css, keyframes } from 'styled-components'

import { ReactComponent as TooltipTriangle } from '../assets/svg/tooltip_triangle.svg'

type ChainAwareLinkProps =
  | (Omit<LinkProps, 'to'> & { to: string; skipChain?: boolean; variant?: 'link' })
  | (Omit<NavLinkProps, 'to'> & { to: string; skipChain?: boolean; variant: 'nav' })

export const ChainAwareLink = ({ to, skipChain = false, variant = 'link', ...props }: ChainAwareLinkProps) => {
  const { chainId } = useConnection()
  if (!to) {
    return <Link {...(props as Omit<LinkProps, 'to'>)} to="" />
  }

  let finalTo = to
  if (!skipChain && chainId) {
    const cleanUrl = to
      .replace(/[?&]chain=[^&?#]*/g, '')
      .replace(/[?&]{2,}/g, '&')
      .replace(/^[?&]/, '?')
      .replace(/[?&]$/, '')
    finalTo = addChainToUrl(cleanUrl, chainId)
  } else {
    finalTo = to
  }

  if (variant === 'nav') {
    return <NavLink {...(props as Omit<NavLinkProps, 'to'>)} to={finalTo} />
  }

  return <Link {...(props as Omit<LinkProps, 'to'>)} to={finalTo} />
}

export const ButtonText = styled.button`
  outline: none;
  border: none;
  font-size: inherit;
  padding: 0;
  margin: 0;
  background: none;
  cursor: pointer;

  :hover {
    opacity: 0.7;
  }

  :focus {
    text-decoration: underline;
  }
  color: ${({ theme }) => theme.textPrimary};
`

export const CloseIcon = styled(X)<{ onClick: () => void }>`
  cursor: pointer;
`

// for wrapper react feather icons
export const IconWrapper = styled.div<{ stroke?: string; size?: string; marginRight?: string; marginLeft?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ size }) => size ?? '20px'};
  height: ${({ size }) => size ?? '20px'};
  margin-right: ${({ marginRight }) => marginRight ?? 0};
  margin-left: ${({ marginLeft }) => marginLeft ?? 0};
  & > * {
    stroke: ${({ theme, stroke }) => stroke ?? theme.textPrimary};
  }
`

// A button that triggers some onClick result, but looks like a link.
export const LinkStyledButton = styled.button<{ disabled?: boolean }>`
  border: none;
  text-decoration: none;
  background: none;

  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  color: ${({ theme, disabled }) => (disabled ? theme.textTertiary : theme.textPrimary)};
  font-weight: 500;

  :hover {
    text-decoration: ${({ disabled }) => (disabled ? null : 'underline')};
  }

  :focus {
    outline: none;
    text-decoration: ${({ disabled }) => (disabled ? null : 'underline')};
  }

  :active {
    text-decoration: none;
  }
`

export const LinkStyle = css`
  text-decoration: none;
  color: ${({ theme }) => theme.buttonPrimary};
  stroke: ${({ theme }) => theme.buttonPrimary};
  cursor: pointer;
  font-weight: 500;

  :hover {
    opacity: 0.9;
  }
  :active {
    opacity: 0.4;
  }
`

// An internal link from the react-router-dom library that is correctly styled
export const StyledInternalLink = styled(ChainAwareLink).attrs({ variant: 'link' })`
  ${LinkStyle}
`

const LinkIconWrapper = styled.a`
  align-items: center;
  justify-content: center;
  display: flex;
`

const CopyIconWrapper = styled.div`
  text-decoration: none;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  display: flex;
`

const IconStyle = css`
  height: 16px;
  width: 18px;
  margin-left: 10px;
`

const LinkIcon = styled(LinkIconFeather)`
  ${IconStyle}
  ${LinkStyle}
`

const CopyIcon = styled(Copy)`
  ${IconStyle}
  ${LinkStyle}
  stroke: ${({ theme }) => theme.accentActive};
  margin: 0px;
`

export const ClockIcon = styled(Clock)<{ size: string }>`
  height: ${({ size }) => size};
  width: ${({ size }) => size};
  stroke: ${({ theme }) => theme.accentActive};
`

export const TrashIcon = styled(Trash)`
  ${IconStyle}
  stroke: ${({ theme }) => theme.textSecondary};

  cursor: pointer;
  align-items: center;
  justify-content: center;
  display: flex;

  :hover {
    opacity: 0.7;
  }
`

const rotateImg = keyframes`
  0% {
    transform: perspective(1000px) rotateY(0deg);
  }

  100% {
    transform: perspective(1000px) rotateY(360deg);
  }
`
function handleClickExternalLink(event: React.MouseEvent<HTMLAnchorElement>) {
  const { target, href } = event.currentTarget
  const isNewTab = target === '_blank' || event.ctrlKey || event.metaKey

  if (isNewTab) {
    // Let the browser handle normal new tab behavior
    return
  }

  // Otherwise, intercept and navigate manually
  event.preventDefault()
  window.location.href = href
}

const StyledLink = styled.a`
  ${LinkStyle}
`
/**
 * Outbound link
 */
export function ExternalLink({
  target = '_blank',
  href,
  rel = 'noopener noreferrer',
  ...rest
}: Omit<HTMLProps<HTMLAnchorElement>, 'as' | 'ref' | 'onClick'> & { href: string }) {
  return <StyledLink target={target} rel={rel} href={href} onClick={handleClickExternalLink} {...rest} />
}

export function ExternalLinkIcon({
  target = '_blank',
  href,
  rel = 'noopener noreferrer',
  ...rest
}: Omit<HTMLProps<HTMLAnchorElement>, 'as' | 'ref' | 'onClick'> & { href: string }) {
  return (
    <LinkIconWrapper target={target} rel={rel} href={href} onClick={handleClickExternalLink} {...rest}>
      <LinkIcon />
    </LinkIconWrapper>
  )
}

const ToolTipWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: absolute;
  transform: translate(5px, 32px);
  z-index: 9999;
`

const CopiedTooltip = styled.div`
  background-color: ${({ theme }) => theme.black};
  text-align: center;
  justify-content: center;
  width: 60px;
  height: 32px;
  line-height: 32px;
  border-radius: 8px;

  color: ${({ theme }) => theme.white};
  font-size: 12px;
`

function ToolTip() {
  return (
    <ToolTipWrapper>
      <TooltipTriangle />
      <CopiedTooltip>Copied!</CopiedTooltip>
    </ToolTipWrapper>
  )
}

export function CopyLinkIcon({ toCopy }: { toCopy: string }) {
  const [isCopied, setCopied] = useCopyClipboard()
  const copy = useCallback(() => {
    setCopied(toCopy)
  }, [toCopy, setCopied])
  return (
    <CopyIconWrapper onClick={copy}>
      <CopyIcon />
      {isCopied && <ToolTip />}
    </CopyIconWrapper>
  )
}

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`
const SpinnerCss = css`
  animation: 2s ${rotate} linear infinite;
`

const Spinner = styled.img`
  ${SpinnerCss}
  width: 16px;
  height: 16px;
`
export const SpinnerSVG = styled.svg`
  ${SpinnerCss}
`

const BackArrowLink = styled(StyledInternalLink)`
  color: ${({ theme }) => theme.deprecated_text1};
`
export function BackArrow({ to }: { to: string }) {
  return (
    <BackArrowLink to={to}>
      <ArrowLeft />
    </BackArrowLink>
  )
}

export const CustomLightSpinner = styled(Spinner)<{ size: string }>`
  height: ${({ size }) => size};
  width: ${({ size }) => size};
`

export const HideSmall = styled.span`
  ${({ theme }) => theme.mediaWidth.upToSmall`
    display: none;
  `};
`

export const HideExtraSmall = styled.span`
  ${({ theme }) => theme.mediaWidth.upToExtraSmall`
    display: none;
  `};
`

export const SmallOnly = styled.span`
  display: none;
  ${({ theme }) => theme.mediaWidth.upToSmall`
    display: block;
  `};
`

export const Separator = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) => theme.deprecated_bg2};
`
